// Wording for tariff quotes and requests. Amounts always come from the server
// (GET /api/tariffs, POST /api/tariffs/quote); nothing here computes a price.
import { formatMoney, formatDate, pluralize } from './format';
import { tokens } from '../theme';

export const monthsLabel = (n) => `${n} ${pluralize(n, 'месяц', 'месяца', 'месяцев')}`;
export const usersLabel = (n) => `${n} ${pluralize(n, 'пользователь', 'пользователя', 'пользователей')}`;
export const accountsLabel = (n) => `${n} ${pluralize(n, 'кабинет', 'кабинета', 'кабинетов')}`;

// The second line under a quote row: how its amount is made up.
export function lineDetail(line, months) {
  const per = months > 1 ? ` × ${months} мес` : '';
  switch (line.kind) {
    case 'module':
      return line.monthly > 0 ? `${formatMoney(line.monthly)} в месяц${per}` : line.note || '';
    case 'accounts':
    case 'users':
      if (line.monthly === 0) return line.note || '';
      return `${line.qty} × ${formatMoney(line.unit)}${per}`;
    case 'bundle':
      return `−${line.percent}% на модули площадок и их кабинеты`;
    case 'period':
      return `−${line.percent}% за оплату на ${monthsLabel(months)}`;
    case 'promo':
      return `−${line.percent}% после скидки за срок`;
    default:
      return '';
  }
}

// Row label with the quantity where it helps: "Доп. кабинеты Wildberries: 2".
export function lineLabel(line) {
  if ((line.kind === 'accounts' || line.kind === 'users') && line.qty) return `${line.label}: ${line.qty}`;
  return line.label;
}

export const isDiscountLine = (line) => line.amount < 0;

export function formatSignedMoney(amount) {
  if (amount < 0) return `−${formatMoney(-amount)}`;
  return formatMoney(amount);
}

export const REQUEST_STATUS = {
  new: { label: 'Новая', bg: tokens.warningSoft, fg: tokens.warning },
  processed: { label: 'Обработана', bg: tokens.successSoft, fg: tokens.success },
  cancelled: { label: 'Отменена', bg: tokens.primaryTint, fg: tokens.inkSoft },
};

export const PROMO_STATE = {
  active: { label: 'Действует', bg: tokens.successSoft, fg: tokens.success },
  disabled: { label: 'Отключён', bg: tokens.primaryTint, fg: tokens.inkSoft },
  expired: { label: 'Истёк', bg: tokens.errorSoft, fg: tokens.error },
  exhausted: { label: 'Исчерпан', bg: tokens.warningSoft, fg: tokens.warning },
};

// Short summary of a request's configuration: "Wildberries · Аналитик (2 кабинета), 3 пользователя, 6 месяцев".
export function requestSummary(request) {
  const modules = (request.quote?.lines || [])
    .filter((l) => l.kind === 'module')
    .map((l) => {
      const acc = request.config?.modules?.find((m) => m.plan === l.plan)?.accounts || 1;
      return acc > 1 ? `${l.label} (${accountsLabel(acc)})` : l.label;
    });
  return [...modules, usersLabel(request.config?.users || 1), monthsLabel(request.months)].join(', ');
}

// Days left until an ISO date (end of that day), never negative.
export function daysLeft(iso, now = Date.now()) {
  if (!iso) return null;
  return Math.max(0, Math.ceil((new Date(iso).getTime() - now) / 86400000));
}

// The user's current access in the tariffs banner.
export function accessSummary(profile, now = Date.now()) {
  if (!profile) return null;
  if (profile.role === 'admin') return { title: 'Администратор', text: 'Полный доступ без ограничения срока' };
  const left = daysLeft(profile.expires_at, now);
  const until = profile.expires_at ? formatDate(profile.expires_at) : null;
  if (profile.is_trial) {
    if (until && left === 0) return { title: 'Пробный период закончился', text: `Закончился ${until}. Оформите заявку, чтобы продолжить работу` };
    return {
      title: 'Сейчас: пробный период',
      text: `Доступно всё, кроме выгрузки отчётов${until ? ` · осталось ${left} ${pluralize(left, 'день', 'дня', 'дней')}, до ${until}` : ''}`,
    };
  }
  if (!until) return { title: 'Сейчас: полный доступ', text: 'Без ограничения срока' };
  if (left === 0) return { title: 'Доступ закончился', text: `Закончился ${until}. Оформите заявку на продление` };
  return { title: 'Сейчас: полный доступ', text: `До ${until} · осталось ${left} ${pluralize(left, 'день', 'дня', 'дней')}` };
}
